import { Injectable } from '@angular/core';
import * as kuromoji from 'kuromoji';
import { LRUCache } from 'lru-cache';
import { ProcessLoadingService } from './loading.service';
import { katakanaToHiragana } from '../_helper/utils';

@Injectable({ providedIn: 'root' })
export class KuromojiService {
  private tokenizer: any = null;
  private initPromise: Promise<void> | null = null;

  constructor(private pageLoadingService: ProcessLoadingService) {
    //this.init();
  }
  /**
   * Khởi tạo tokenizer (chỉ chạy 1 lần, các lần gọi sau sẽ dùng lại)
   */
  init(): Promise<void> {
  
    if (this.initPromise) {
      return this.initPromise;
    }

    this.initPromise = new Promise<void>((resolve, reject) => {
      kuromoji
        .builder({ dicPath: 'assets/dict/' })
        .build((err: any, tokenizer: any) => {
          if (err) {
            this.initPromise = null;
            reject(err);
            return;
          }
          this.tokenizer = tokenizer;
          resolve();
        });
    });

    return this.initPromise;
  }

  /**
   * Phân tích câu tiếng Nhật thành các token
   * @param text Chuỗi tiếng Nhật cần phân tích
   * @returns Mảng các token
   */
  async tokenize(text: string): Promise<any[]> {
    await this.ensureReady(); // Đảm bảo tokenizer đã sẵn sàng trước khi gọi tokenize
    if (!this.tokenizer) {
   
      throw new Error('Tokenizer chưa được khởi tạo. Hãy gọi await init() trước.');
    }
    return this.tokenizer.tokenize(text);
  }

  /**
   * Kiểm tra tokenizer đã sẵn sàng chưa
   */
  isReady(): boolean {
    return this.tokenizer !== null;
  }

  /**
   * Đợi tokenizer khởi tạo xong. Dùng trong các method async cần tokenizer.
   */
  async ensureReady(): Promise<void> {
    this.pageLoadingService.showLoading();
    await this.init();
    this.pageLoadingService.hideLoading();
  }


  /**
   * LRU Cache lưu trữ kết quả chuyển đổi Kanji/Text → Hiragana.
   * - Key: chuỗi gốc (Kanji/text)
   * - Value: chuỗi Hiragana đã chuyển đổi
   * - max: tối đa 100,000 entries
   * - ttl: tự động xóa entry sau 30 phút
   *
   * Được sử dụng trong sortData() để tránh gọi tokenizer.tokenize() lặp lại
   * cho cùng một chuỗi, giúp tăng hiệu năng khi sắp xếp danh sách lớn.
   */
  hiraganaCache = new LRUCache<string, string>({
    max: 100000,
    ttl: 1000 * 60 * 30 //30 min
  });

  katakanaToRomanjiMap: Record<string, string> = {
    // Basic vowels
    "ア": "a", "イ": "i", "ウ": "u", "エ": "e", "オ": "o",
    // K-row
    "カ": "ka", "キ": "ki", "ク": "ku", "ケ": "ke", "コ": "ko",
    // S-row (Hepburn: "シ" = "shi" instead of "si")
    "サ": "sa", "シ": "shi", "ス": "su", "セ": "se", "ソ": "so",
    // T-row (Hepburn: "チ" = "chi", "ツ" = "tsu")
    "タ": "ta", "チ": "chi", "ツ": "tsu", "テ": "te", "ト": "to",
    // N-row
    "ナ": "na", "ニ": "ni", "ヌ": "nu", "ネ": "ne", "ノ": "no",
    // H-row (Hepburn: "フ" = "fu" instead of "hu")
    "ハ": "ha", "ヒ": "hi", "フ": "fu", "ヘ": "he", "ホ": "ho",
    // M-row
    "マ": "ma", "ミ": "mi", "ム": "mu", "メ": "me", "モ": "mo",
    // Y-row
    "ヤ": "ya", "ユ": "yu", "ヨ": "yo",
    // R-row
    "ラ": "ra", "リ": "ri", "ル": "ru", "レ": "re", "ロ": "ro",
    // W-row
    "ワ": "wa", "ヲ": "o", "ン": "n",
    // Dakuten sounds (voiced consonants)
    "ガ": "ga", "ギ": "gi", "グ": "gu", "ゲ": "ge", "ゴ": "go",
    "ザ": "za", "ジ": "ji", "ズ": "zu", "ゼ": "ze", "ゾ": "zo",
    "ダ": "da", "ヂ": "ji", "ヅ": "zu", "デ": "de", "ド": "do",
    "バ": "ba", "ビ": "bi", "ブ": "bu", "ベ": "be", "ボ": "bo",
    // Handakuten sounds (p-sound)
    "パ": "pa", "ピ": "pi", "プ": "pu", "ペ": "pe", "ポ": "po",
    // Small kana combinations (e.g., "キャ" = "kya")
    "キャ": "kya", "キュ": "kyu", "キョ": "kyo",
    "シャ": "sha", "シュ": "shu", "ショ": "sho",
    "チャ": "cha", "チュ": "chu", "チョ": "cho",
    "ニャ": "nya", "ニュ": "nyu", "ニョ": "nyo",
    "ヒャ": "hya", "ヒュ": "hyu", "ヒョ": "hyo",
    "ミャ": "mya", "ミュ": "myu", "ミョ": "myo",
    "リャ": "rya", "リュ": "ryu", "リョ": "ryo",
    "ギャ": "gya", "ギュ": "gyu", "ギョ": "gyo",
    "ジャ": "ja", "ジュ": "ju", "ジョ": "jo",
    "ビャ": "bya", "ビュ": "byu", "ビョ": "byo",
    "ピャ": "pya", "ピュ": "pyu", "ピョ": "pyo",
    // Special cases
    "ッ": "", // Small "ッ" doubles the next consonant
    "ー": "" // Prolonged sound marker
  };

  // Mapping Half-width Katakana (1-byte) to Full-width Katakana (2-byte)
  halfWidthToFullWidth: Record<string, string> = {
    "ｱ": "ア", "ｲ": "イ", "ｳ": "ウ", "ｴ": "エ", "ｵ": "オ",
    "ｶ": "カ", "ｷ": "キ", "ｸ": "ク", "ｹ": "ケ", "ｺ": "コ",
    "ｻ": "サ", "ｼ": "シ", "ｽ": "ス", "ｾ": "セ", "ｿ": "ソ",
    "ﾀ": "タ", "ﾁ": "チ", "ﾂ": "ツ", "ﾃ": "テ", "ﾄ": "ト",
    "ﾅ": "ナ", "ﾆ": "ニ", "ﾇ": "ヌ", "ﾈ": "ネ", "ﾉ": "ノ",
    "ﾊ": "ハ", "ﾋ": "ヒ", "ﾌ": "フ", "ﾍ": "ヘ", "ﾎ": "ホ",
    "ﾏ": "マ", "ﾐ": "ミ", "ﾑ": "ム", "ﾒ": "メ", "ﾓ": "モ",
    "ﾔ": "ヤ", "ﾕ": "ユ", "ﾖ": "ヨ",
    "ﾗ": "ラ", "ﾘ": "リ", "ﾙ": "ル", "ﾚ": "レ", "ﾛ": "ロ",
    "ﾜ": "ワ", "ｦ": "ヲ", "ﾝ": "ン",
    "ｧ": "ァ", "ｨ": "ィ", "ｩ": "ゥ", "ｪ": "ェ", "ｫ": "ォ",
    "ｬ": "ャ", "ｭ": "ュ", "ｮ": "ョ", "ｯ": "ッ",
    "ｰ": "ー" // Prolonged sound marker
  };

  // Dakuten & Handakuten mappings
  dakutenMap: Record<string, string> = {
    "ｶﾞ": "ガ", "ｷﾞ": "ギ", "ｸﾞ": "グ", "ｹﾞ": "ゲ", "ｺﾞ": "ゴ",
    "ｻﾞ": "ザ", "ｼﾞ": "ジ", "ｽﾞ": "ズ", "ｾﾞ": "ゼ", "ｿﾞ": "ゾ",
    "ﾀﾞ": "ダ", "ﾁﾞ": "ヂ", "ﾂﾞ": "ヅ", "ﾃﾞ": "デ", "ﾄﾞ": "ド",
    "ﾊﾞ": "バ", "ﾋﾞ": "ビ", "ﾌﾞ": "ブ", "ﾍﾞ": "ベ", "ﾎﾞ": "ボ"
  };
  handakutenMap: Record<string, string> = {
    "ﾊﾟ": "パ", "ﾋﾟ": "ピ", "ﾌﾟ": "プ", "ﾍﾟ": "ペ", "ﾎﾟ": "ポ"
  };

  // Convert Half-width Katakana to Full-width Katakana (including dakuten & handakuten)
  convertHalfWidthToFullWidth(input: string) {
    let result = "";
    for (let i = 0; i < input.length; i++) {
      let char = input[i];
      let nextChar = input[i + 1];

      // Check for dakuten (ﾞ) and handakuten (ﾟ)
      if (nextChar === "ﾞ" || nextChar === "ﾟ") {
        let combined = char + nextChar;
        if (this.dakutenMap[combined]) {
          result += this.dakutenMap[combined];
          i++; // Skip next character
          continue;
        } else if (this.handakutenMap[combined]) {
          result += this.handakutenMap[combined];
          i++; // Skip next character
          continue;
        }
      }

      // Normal half-width to full-width conversion
      result += this.halfWidthToFullWidth[char] || char;
    }
    return result;
  }

  // Main function to convert Katakana (Half-width or Full-width) to Hepburn Romanization
  katakanaToHepburn(katakana: string) {
    // Convert half-width Katakana to full-width
    katakana = this.convertHalfWidthToFullWidth(katakana);

    let result = "";

    for (let i = 0; i < katakana.length; i++) {
      let char = katakana[i];

      // Handle small kana combinations (e.g., "キャ" -> "kya")
      if (i < katakana.length - 1) {
        let nextChar = katakana[i + 1];
        let combined = char + nextChar;
        if (this.katakanaToRomanjiMap[combined]) {
          result += this.katakanaToRomanjiMap[combined];
          i++; // Skip the next character
          continue;
        }
      }

      // Handle small "ッ" (sokuon) which doubles the next consonant
      if (char === "ッ" && i < katakana.length - 1) {
        let nextRomanji = this.katakanaToRomanjiMap[katakana[i + 1]];
        if (nextRomanji) {
          result += nextRomanji[0]; // Double the first consonant
        }
        continue;
      }

      // Handle "ー" (prolonged sound marker)
      if (char === "ー" && result.length > 0) {
        result += result[result.length - 1]; // Repeat the last vowel
        continue;
      }

      // Convert regular Katakana characters
      result += this.katakanaToRomanjiMap[char] || char;
    }

    // Hepburn rule: "ン" before 'b', 'm', 'p' becomes "m"
    result = result.replace(/n([bmp])/g, "m$1");

    return result;
  }


  // Chuyển đổi văn bản Kanji sang Hiragana
  convertToHiragana(kanjiText: string, tokenizer: any): string {
    if (!kanjiText) return "";
    if (!tokenizer || !tokenizer.tokenize) return kanjiText;

    const normalized = this.normalizeString(kanjiText);
    const tokens = tokenizer.tokenize(normalized);

    let hiragana = '';
    tokens.forEach((token: any) => {
      if (token.reading) {
        // Chuyển Katakana thành Hiragana
        hiragana += katakanaToHiragana(token.reading);
      } else {
        // Giữ nguyên surface nếu không có cách đọc
        hiragana += token.surface_form;
      }
    });
    return hiragana;
  }


  normalizeString(input: string): string {
    // Chuyển số full-width sang half-width
    const fullWidthToHalfWidth = (str: string) =>
      str.replace(/[\uFF01-\uFF5E]/g, (char) =>
        String.fromCharCode(char.charCodeAt(0) - 0xFEE0)
      ).replace(/\u3000/g, ' '); // Thay khoảng trắng full-width thành khoảng trắng thường

    const normalized = fullWidthToHalfWidth(input);
    return normalized;
  }

  async searchUserName(keyword: string, list: any[]): Promise<any[]> {
    if (!Array.isArray(list)) {
      return [];
    }

    const normalizedKeyword = keyword?.trim();
    if (!normalizedKeyword) {
      return list;
    }

    if (!this.tokenizer) {
      await this.ensureReady();
    }

    const keywordHiragana = this.normalizeForSearch(normalizedKeyword);

    return list.filter(user => {
      const userName = user?.username ?? user?.name ?? '';
      const fullNameKana = user?.fullnameKana ?? user?.kanaFullName ?? user?.kanafullname ?? '';
      console.log(fullNameKana, katakanaToHiragana(fullNameKana))
      return (
        this.normalizeForSearch(userName).includes(keywordHiragana) ||
        this.normalizeForSearch(fullNameKana).includes(keywordHiragana)||
        this.converHiragana(fullNameKana).includes(keywordHiragana) ||
        this.converHiragana(userName).includes(keywordHiragana) ||
        katakanaToHiragana(fullNameKana).includes(keywordHiragana)
      );
    });
  }

   private normalizeForSearch(value: unknown): string {
    const text = String(value ?? '');
    const fullWidthKana = this.convertHalfWidthToFullWidth(text);

    return fullWidthKana.replace(/\s+/g, '').toLocaleLowerCase();
  }

  private converHiragana(value: unknown): string {
    const text = String(value ?? '');
    const fullWidthKana = this.convertHalfWidthToFullWidth(text);
    let hiragana = this.hiraganaCache.get(fullWidthKana);

    if (hiragana === undefined) {
      hiragana = this.convertToHiragana(fullWidthKana, this.tokenizer);
      this.hiraganaCache.set(fullWidthKana, hiragana);
    }

    return hiragana.replace(/\s+/g, '').toLocaleLowerCase();
  }

  async sortData(
    list: any[],
    order: 'asc' | 'desc' = 'asc',
    key?: string,
  ): Promise<any[]> {

    if(!this.tokenizer) {
      await this.ensureReady();
      //return list;
    }
    
    return list.sort((a, b) => {
      const aValue =
        typeof a === 'string'
          ? a
          : key && typeof a[key] === 'string'
            ? a[key]
            : '';
      const bValue =
        typeof b === 'string'
          ? b
          : key && typeof b[key] === 'string'
            ? b[key]
            : '';

      let aHiragana = this.hiraganaCache.get(aValue);
      if (!aHiragana && aValue) {
        aHiragana = this.convertToHiragana(aValue, this.tokenizer);
        this.hiraganaCache.set(aValue, aHiragana);
      }

      let bHiragana = this.hiraganaCache.get(bValue);
      if (!bHiragana && bValue) {
        bHiragana = this.convertToHiragana(bValue, this.tokenizer);
        this.hiraganaCache.set(bValue, bHiragana);
      }

      aHiragana = aHiragana ?? '';
      bHiragana = bHiragana ?? '';

      const comparison = aHiragana.localeCompare(bHiragana, 'ja');
      return order === 'asc' ? comparison : -comparison;
    });
  }
}
